import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import PartnerMainContainer from '../../../components/PartnerMainContainer';
import TitlePartner from '../component/Title';
import PartnerService from '../../../services/PartnerService';
import PartnerEvalution from './PartnerEvalution';
import partnersListStyles from './partnerslist.styles';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { financements, situations } from '../constants/constants';
import {
  formatDate,
  formatPartnerSource,
  isValidValue,
  normalizeFinancement,
  normalizeText,
} from '../../../helpers/PartnerHelper';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import DirectionService from '../../../services/DirectionService';
import ExportContainer from '../Export/ExportContainer';
import { getPID, getSpmPID } from '../../../helpers/PeidGeneratorHelper';
import usePartnerFilterConsultation from '../hooks/usePartnerFilterConsultation';
import { exporterTableauPartenaires } from '../Export/exporterTableauPartenaires';
import {
  partnerDataGridSx as partnerGridSx,
  partnerFilterButtonSx as filterButtonSx,
  partnerFilterFieldSx as filterFieldSx,
  partnerGroupButtonSx as groupButtonSx,
} from './partnerListTheme';

const getEvaluationMeta = (value) => { // normalise les valeurs d'évaluation pour l'affichage et le tri
  const mapping = {
    'BONNE PERFORMANCE': { label: 'Bonne perf.', key: 'good' },
    'PERFORMANCE MOYENNE': { label: 'Moy.', key: 'average' },
    'PERFORMANCE INSUFFISANTE': { label: 'Insuffisant', key: 'poor' },
    ACCEPTABLE: { label: 'Acceptable', key: 'acceptable' },
  };
  return mapping[value] || { label: value || 'Manquant', key: value ? 'acceptable' : 'missing' };
};

const hasIntersection = (rowValues, selectedValues) => { // vérifie si les valeurs de la ligne ont une intersection avec les valeurs sélectionnées pour le filtrage
  if (!Array.isArray(selectedValues) || selectedValues.length === 0) return true;
  return rowValues.some((value) => selectedValues.includes(value));
};

const cloneFilters = (filters) => ({ // assure que les tableaux de filtres sont toujours des nouvelles instances pour éviter les problèmes de référence
  directions: Array.isArray(filters?.directions) ? [...filters.directions] : [],
  financements: Array.isArray(filters?.financements) ? [...filters.financements] : [],
  statuts: Array.isArray(filters?.statuts) ? [...filters.statuts] : [],
  situations: Array.isArray(filters?.situations) ? [...filters.situations] : [],
  search: filters?.search || '',
  sortBy: filters?.sortBy || 'name',
  order: filters?.order || 'asc',
  dateFrom: filters?.dateFrom || '',
  dateTo: filters?.dateTo || '',
  groupBy: filters?.groupBy || 'none',
});

const PartenersList = () => {
  const classes = partnersListStyles();
  const [partnersSource, setPartnersSource] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [directions, setDirections] = useState([]);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const fetchDirections = async () => {
    try {
      const result = await DirectionService.getDirection();
      if (result?.status === 1 && Array.isArray(result.data)) {
        setDirections(result.data.map((dir) => String(dir.code).trim()).filter(isValidValue));
      } else {
        setDirections([]);
      }
    } catch {
      setDirections([]);
    }
  };

  useEffect(() => {
    fetchDirections();
  }, []);

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    PartnerService.getAllPartners()
      .then((result) => {
        if (!isMounted) return;

        const data = Array.isArray(result?.data) ? result.data : [];
        setPartnersSource(data);

        if (result?.status === 1 || data.length > 0) {
          setLoadError('');
        } else {
          setLoadError(result?.message || 'Impossible de charger les partenaires.');
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setPartnersSource([]);
        setLoadError('Impossible de charger les partenaires.');
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

    // Gestion du menu d'exportation
  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };
  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const baseRows = useMemo(() => { // prépare les données des partenaires en nettoyant les valeurs et en calculant les champs dérivés nécessaires pour l'affichage et les filtres
    return partnersSource
      .map((row, index) => {
        const partnerName = isValidValue(row?.partenaire) ? String(row.partenaire).trim() : '-';
        const statutValues = Array.isArray(row.statutValues)
          ? row.statutValues.filter(isValidValue).map((value) => String(value).trim()).sort()
          : [];
        const directionValues = Array.isArray(row.directionValues)
          ? row.directionValues.filter(isValidValue).map((value) => String(value).trim()).sort()
          : [];
        const financementValues = Array.isArray(row.financementValues)
          ? [...new Set(row.financementValues.filter(isValidValue).map(normalizeFinancement).filter(isValidValue))].sort()
          : [];
        const evaluation = row.evaluation || '';
        const situation = row.situation || 'ACHEVÉ';

        return {
          id: row.id || row.partenaire_id || ('SPM-' + (index + 1)),
          partenaireId: row.partenaire_id || null,
          partenaire: partnerName,
          partnerPid: row.read_only === true ? getSpmPID(row.id || index + 1) : getPID(row.partenaire_id),
          sourceDb: row.source_db || 'bdd partenaire',
          sourceLabel: formatPartnerSource(row.source_db),
          readOnly: row.read_only === true || !row.partenaire_id,
          isCI: row.isCI === true || normalizeText(row.statutJuridique).includes('CONSULTANT'),
          isMulti: row.isMulti === true,
          statutJuridique:
            statutValues.join(' / ') ||
            (isValidValue(row.statutJuridique) ? String(row.statutJuridique).trim() : '-'),
          directions:
            (isValidValue(row.directions) ? String(row.directions).trim() : '') ||
            directionValues.join(', ') ||
            '-',
          nbMarches: Number(row.nbMarches) || 0,
          evaluation,
          evaluationDate: isValidValue(row.evaluationDate) ? String(row.evaluationDate) : null,
          situation,
          statutValues,
          directionValues,
          financementValues,
          contractPeriods: Array.isArray(row.contractPeriods)
            ? row.contractPeriods.map((period) => ({
                dateDebut: isValidValue(period?.dateDebut) ? String(period.dateDebut) : '',
                dateFin: isValidValue(period?.dateFin) ? String(period.dateFin) : '',
              }))
            : [],
        };
      })
      .sort((a, b) => a.partenaire.localeCompare(b.partenaire));
  }, [partnersSource]);

  // Fonction pour obtenir le résumé des filtres sous forme de texte
  const getFiltersSummary = () => {
    const filters = [];
    const isPartialSelection = (selectedValues, allValues) => (
      Array.isArray(selectedValues)
      && Array.isArray(allValues)
      && selectedValues.length > 0
      && selectedValues.length < allValues.length
    );
    
    if (appliedFilters.search) filters.push(`Recherche: "${appliedFilters.search}"`);
    if (isPartialSelection(appliedFilters.directions, directionOptions)) {
      filters.push(`Directions: ${appliedFilters.directions.join(', ')}`);
    }
    if (isPartialSelection(appliedFilters.financements, fundingOptions)) {
      filters.push(`Financements: ${appliedFilters.financements.join(', ')}`);
    }
    if (isPartialSelection(appliedFilters.statuts, statutOptions)) {
      filters.push(`Statut juridique: ${appliedFilters.statuts.join(', ')}`);
    }
    if (isPartialSelection(appliedFilters.situations, situationOptions)) {
      filters.push(`Situations: ${appliedFilters.situations.join(', ')}`);
    }
    if (appliedFilters.dateTo) filters.push(`Début ≥ ${formatDate(appliedFilters.dateTo)}`);
    if (appliedFilters.dateFrom) filters.push(`Fin ≤ ${formatDate(appliedFilters.dateFrom)}`);
    
    return filters;
  };

  const exporter = (format) => {
    const lignes = rows.map((row) => ({
      Identifiant: row.partnerPid || '', Partenaire: row.partenaire || '',
      CI: row.isCI ? 'Oui' : 'Non', Multi: row.isMulti ? 'Oui' : 'Non',
      'Statut juridique': row.statutJuridique || '', 'Direction(s)': row.directions || '',
      'Nombre de marches': row.nbMarches || 0, Evaluation: row.evaluation || '',
      'Date evaluation': formatDate(row.evaluationDate), Situation: row.situation || '',
      'Base source': row.sourceLabel || formatPartnerSource(row.sourceDb),
    }));
    const filtres = getFiltersSummary();
    const dateExport = new Date().toLocaleString('fr-FR');
    try {
      exporterTableauPartenaires({
        format, lignes,
        entetes: ['Identifiant', 'Partenaire', 'CI', 'Multi', 'Statut juridique', 'Direction(s)',
          'Nombre de marches', 'Evaluation', 'Date evaluation', 'Situation', 'Base source'],
        titre: 'Rapport des partenaires',
        nomFichier: `partenaires_${new Date().toISOString().split('T')[0]}`,
        nomFeuille: 'Donnees',
        resume: `Nombre de partenaires : ${rows.length}`,
        filtres,
        rapportExcel: ({ colonnes, valeurs }) => [
          ['RAPPORT DES PARTENAIRES'], ["Date d'exportation", dateExport],
          ['Nombre de partenaires', rows.length], [],
          ...(filtres.length ? [['FILTRES APPLIQUES'], ...filtres.map((filtre) => [filtre]), []] : []),
          colonnes, ...valeurs,
        ],
        pdf: {
          entetes: ['ID', 'Partenaire', 'Statut jur.', 'Direction(s)', 'Marches',
            'Evaluation', 'Date eval.', 'Situation', 'Base source'],
          lignes: rows.map((row) => [row.partnerPid || '', row.partenaire || '',
            row.statutJuridique || '', row.directions || '', String(row.nbMarches || 0),
            getEvaluationMeta(row.evaluation).label, formatDate(row.evaluationDate),
            row.situation || '', row.sourceLabel || formatPartnerSource(row.sourceDb)]),
        },
        imprimerAutomatiquement: false,
        audit: { menu: 'partners', format: format === 'xlsx' ? 'xlsx' : format,
          filters: appliedFilters, rowCount: rows.length },
      });
    } catch (error) {
      console.error(`Erreur lors de l'export ${format} :`, error);
    }
  };
  const handleExcelExport = () => exporter('xlsx');
  const handleCsvExport = () => exporter('csv');
  const handlePdfExport = () => exporter('pdf');
  const handlePrint = () => exporter('print');
  const directionOptions = directions.sort((a, b) => a.localeCompare(b, 'fr'));
  const statutOptions = useMemo(
    () => [...new Set(baseRows.flatMap((row) => row.statutValues))].sort((a, b) => a.localeCompare(b, 'fr')),
    [baseRows]
  );

  const fundingOptions = useMemo(
    () => [...new Set([...(financements || []), ...baseRows.flatMap((row) => row.financementValues)])]
      .filter(isValidValue)
      .sort((a, b) => a.localeCompare(b, 'fr')),
    [baseRows]
  );

  const situationOptions = useMemo(
    () => [...new Set([...(situations || []), ...baseRows.map((row) => row.situation)])].filter(isValidValue),
    [baseRows]
  );

  const defaultFilters = useMemo(
    () => ({
      directions: [],
      financements: [],
      statuts: [],
      situations: [],
      search: '',
      sortBy: 'name',
      order: 'asc',
      dateFrom: '',
      dateTo: '',
      groupBy: 'none',
    }),
    []
  );

  const [pendingFilters, setPendingFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [selectedPartnerName, setSelectedPartnerName] = useState('');
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [selectedPartnerPid, setSelectedPartnerPid] = useState('');
  const [selectedPartnerSourceDb, setSelectedPartnerSourceDb] = useState('');
  const [isEvaluationDialogOpen, setIsEvaluationDialogOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  usePartnerFilterConsultation(pendingFilters, (filters) => PartnerService.getAllPartners(filters));

  const handleResetFilters = () => {
    const resetState = cloneFilters(defaultFilters);
    setPendingFilters(resetState);
    setAppliedFilters(cloneFilters(resetState));
  };

  React.useEffect(() => {
    const nextFilters = cloneFilters(defaultFilters);
    setPendingFilters(nextFilters);
    setAppliedFilters(cloneFilters(nextFilters));
  }, [defaultFilters]);

  React.useEffect(() => {
    setAppliedFilters(cloneFilters(pendingFilters));
  }, [pendingFilters]);

  React.useEffect(() => {
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, [appliedFilters]);

  const activeFiltersCount = useMemo(() => (
    pendingFilters.directions.length
    + pendingFilters.financements.length
    + pendingFilters.statuts.length
    + pendingFilters.situations.length
    + (pendingFilters.search ? 1 : 0)
    + (pendingFilters.dateFrom ? 1 : 0)
    + (pendingFilters.dateTo ? 1 : 0)
    + (pendingFilters.groupBy !== 'none' ? 1 : 0)
    + (pendingFilters.sortBy !== 'name' ? 1 : 0)
    + (pendingFilters.order !== 'asc' ? 1 : 0)
  ), [pendingFilters]);

  const rows = useMemo(() => {
    setIsLoading(true);
    const filteredRows = baseRows.filter((row) => {
      const directionOk = hasIntersection(row.directionValues, appliedFilters.directions);
      const fundingOk = hasIntersection(row.financementValues, appliedFilters.financements);
      const statutOk = hasIntersection(row.statutValues, appliedFilters.statuts);
      const situationOk = hasIntersection([row.situation], appliedFilters.situations);
      const searchOk =
        !appliedFilters.search ||
        row.partenaire.toLowerCase().includes(String(appliedFilters.search).toLowerCase().trim());
      let periodOk = true;

      if (appliedFilters.dateFrom || appliedFilters.dateTo) {
        const periods = Array.isArray(row.contractPeriods) ? row.contractPeriods : [];
        periodOk = periods.some((period) => {
          const dateDebut = period?.dateDebut || '';
          const dateFin = period?.dateFin || '';
          if (appliedFilters.dateFrom && dateFin && dateFin < appliedFilters.dateFrom) return false;
          if (appliedFilters.dateTo && dateDebut && dateDebut > appliedFilters.dateTo) return false;
          return true;
        });
      }

      return directionOk && fundingOk && statutOk && situationOk && searchOk && periodOk;
    });

    const sortedRows = [...filteredRows].sort((a, b) => {
      if (appliedFilters.sortBy === 'nbMarches') return a.nbMarches - b.nbMarches;
      if (appliedFilters.sortBy === 'situation') return a.situation.localeCompare(b.situation, 'fr');
      if (appliedFilters.sortBy === 'evalResult') return a.evaluation.localeCompare(b.evaluation, 'fr');
      return a.partenaire.localeCompare(b.partenaire, 'fr');
    });

    setIsLoading(false);
    return appliedFilters.order === 'desc' ? sortedRows.reverse() : sortedRows;
  }, [baseRows, appliedFilters]);

  const groupedRows = useMemo(() => {
    if (appliedFilters.groupBy === 'none') return [];

    const missingLabels = {
      direction: 'Aucune direction',
      financement: 'Aucun financement',
      statutJuridique: 'Aucun statut juridique',
      situation: 'Aucune situation',
    };
    const groups = new Map();
    rows.forEach((row) => {
      let groupKey = '';
      if (appliedFilters.groupBy === 'direction') {
        groupKey = row.directionValues?.[0] || '';
      } else if (appliedFilters.groupBy === 'financement') {
        groupKey = row.financementValues?.[0] || '';
      } else if (appliedFilters.groupBy === 'statutJuridique') {
        groupKey = row.statutJuridique || '';
      } else if (appliedFilters.groupBy === 'situation') {
        groupKey = row.situation || '';
      }

      if (!isValidValue(groupKey)) {
        groupKey = missingLabels[appliedFilters.groupBy];
      }

      if (!groups.has(groupKey)) groups.set(groupKey, []);
      groups.get(groupKey).push(row);
    });

    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b, 'fr'));
  }, [rows, appliedFilters.groupBy]);

  const groupedPages = useMemo(() => {
    if (appliedFilters.groupBy === 'none') return [];

    const pages = [];
    let currentPage = [];
    let currentPartnerCount = 0;

    groupedRows.forEach(([groupName, groupItems], groupIndex) => {
      if (
        currentPartnerCount > 0
        && currentPartnerCount + groupItems.length > paginationModel.pageSize
      ) {
        pages.push(currentPage);
        currentPage = [];
        currentPartnerCount = 0;
      }

      currentPage.push({
        id: `GROUP-${appliedFilters.groupBy}-${groupIndex}`,
        partenaire: `${groupName} (${groupItems.length})`,
        isGroupHeader: true,
      });
      currentPage.push(...groupItems);
      currentPartnerCount += groupItems.length;
    });

    if (currentPage.length > 0) pages.push(currentPage);
    return pages;
  }, [appliedFilters.groupBy, groupedRows, paginationModel.pageSize]);

  const displayedRows = useMemo(() => {
    if (appliedFilters.groupBy === 'none') return rows;
    return groupedPages[paginationModel.page] || groupedPages[0] || [];
  }, [appliedFilters.groupBy, groupedPages, paginationModel.page, rows]);

  React.useEffect(() => {
    if (
      appliedFilters.groupBy !== 'none'
      && paginationModel.page >= Math.max(groupedPages.length, 1)
    ) {
      setPaginationModel((current) => ({ ...current, page: 0 }));
    }
  }, [appliedFilters.groupBy, groupedPages.length, paginationModel.page]);

  const handleOpenEvaluationDialog = useCallback((
    partnerName,
    partnerId = null,
    partnerPid = '',
    sourceDb = ''
  ) => {
    if (!partnerId && (!isValidValue(partnerName) || partnerName === '-')) return;
    setSelectedPartnerName(partnerName || '');
    setSelectedPartnerId(partnerId || null);
    setSelectedPartnerPid(partnerPid || '');
    setSelectedPartnerSourceDb(sourceDb || '');
    setIsEvaluationDialogOpen(true);
  }, []);

  const handleCloseEvaluationDialog = () => {
    setIsEvaluationDialogOpen(false);
  };

  const columns = useMemo(
    () => [
      {
        field: 'partenaire',
        headerName: 'PARTENAIRE',
        flex: 2.8,
        renderCell: (params) => {
          if (params.row.isGroupHeader) {
            return <span className={classes.groupGridLabel}>{params.value}</span>;
          }

          return (
            <div className={classes.partnerCell}>
              <div className={classes.partnerMainLine}>
                {params.row.isCI ? <span className={`${classes.badge} ${classes.badgeCI}`}>CI</span> : null}
                {params.row.isMulti ? (
                  <span className={`${classes.badge} ${classes.badgeMulti}`}>MULTI</span>
                ) : null}
                <span className={classes.partnerName}>{params.value}</span>
              </div>
              <span className={classes.partnerPid}>{params.row.partnerPid || '—'}</span>
            </div>
          );
        },
      },
      {
        field: 'statutJuridique',
        headerName: 'STATUT JUR.',
        flex: 1.2,
        renderCell: (params) => params.row.isGroupHeader ? null : (
          <span className={`${classes.badge} ${classes.badgeLegal}`}>{params.value || '-'}</span>
        ),
      },
      {
        field: 'directions',
        headerName: 'DIRECTION(S)',
        flex: 1.2,
      },
      {
        field: 'nbMarches',
        headerName: 'MARCHES',
        type: 'number',
        flex: 0.7,
        renderCell: (params) => params.row.isGroupHeader ? null : <span className={classes.monoCell}>{params.value}</span>,
      },
      {
        field: 'evaluation',
        headerName: 'EVALUATION',
        flex: 1,
        renderCell: (params) => {
          if (params.row.isGroupHeader) return null;
          const meta = getEvaluationMeta(params.value);
          const evalClass = {
            good: classes.badgeEvalGood,
            average: classes.badgeEvalAverage,
            poor: classes.badgeEvalPoor,
            acceptable: classes.badgeEvalAcceptable,
            missing: classes.badgeMissing,
          }[meta.key];

          return <span className={`${classes.badge} ${evalClass}`}>{meta.label}</span>;
        },
      },
      {
        field: 'evaluationDate',
        headerName: 'DATE EVAL.',
        flex: 0.9,
        renderCell: (params) => params.row.isGroupHeader ? null : <span className={classes.monoCell}>{formatDate(params.value)}</span>,
      },
      {
        field: 'situation',
        headerName: 'SITUATION',
        flex: 0.9,
        renderCell: (params) => {
          if (params.row.isGroupHeader) return null;
          const situationClass =
            params.value === 'EN COURS'
              ? classes.badgeSituationEncours
              : params.value === 'RESILIE'
                ? classes.badgeSituationResilie
                : classes.badgeSituationFin;

          return <span className={`${classes.badge} ${situationClass}`}>{params.value || '-'}</span>;
        },
      },
      {
        field: 'sourceLabel',
        headerName: 'BASE SOURCE',
        flex: 1.3,
        renderCell: (params) => params.row.isGroupHeader ? null : (
          <span className={classes.badge}>{params.value || formatPartnerSource(params.row.sourceDb)}</span>
        ),
      },
      {
        field: 'fiche',
        headerName: 'FICHE',
        flex: 0.5,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => params.row.isGroupHeader ? null : (
          <Tooltip title={params.row.readOnly ? "Fiche partenaire SPM en lecture seule" : "Fiche partenaire"}>
            <IconButton
              size="small"
              disabled={!params.row.partenaireId && params.row.partenaire === '-'}
              className={classes.ficheButton}
              color='primary'
              onClick={(event) => {
                event.stopPropagation();
                handleOpenEvaluationDialog(
                  params.row.partenaire,
                  params.row.partenaireId,
                  params.row.partnerPid,
                  params.row.sourceDb
                );
              }}
              onDoubleClick={(event) => event.stopPropagation()}
            >
              📋
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [classes, handleOpenEvaluationDialog]
  );

  return (
    <PartnerMainContainer>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={isLoading} />
      <Box className={classes.root}>
        <div className={classes.titleRow}>
          <TitlePartner title="Partenaires" sub={`(${rows.length} partenaires)`} icon="🏢" />
        </div>

        {loadError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        ) : null}

        <Paper
          elevation={0}
          sx={{ border: '1px solid #cbd8e6', borderRadius: '8px', p: 1.25 }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              justifyContent: 'space-between',
              mb: filtersVisible ? 1.5 : 0,
            }}
          >
            <Typography
              color="#52677c"
              fontSize={10}
              fontWeight={700}
              letterSpacing="0.05em"
            >
              FILTRES
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: 'flex-end',
                ml: { sm: 'auto' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {filtersVisible && (
                <Button
                  color="inherit"
                  disabled={activeFiltersCount === 0}
                  onClick={handleResetFilters}
                  size="small"
                  startIcon={<RestartAltIcon />}
                  sx={{
                    ...filterButtonSx,
                    borderColor: '#9fb1c3',
                    color: '#173d61',
                    '&:hover': { backgroundColor: '#f3f7fa', borderColor: '#173d61' },
                  }}
                  variant="outlined"
                >
                  Réinitialiser
                </Button>
              )}
              <Button
                color="inherit"
                onClick={() => setFiltersVisible((visible) => !visible)}
                size="small"
                startIcon={filtersVisible ? <FilterAltOffOutlinedIcon /> : <FilterAltOutlinedIcon />}
                sx={{
                  ...filterButtonSx,
                  borderColor: '#9fb1c3',
                  color: '#173d61',
                  '&:hover': { backgroundColor: '#f3f7fa', borderColor: '#173d61' },
                }}
                variant="outlined"
              >
                {filtersVisible ? 'Masquer filtres' : 'Afficher filtres'}
              </Button>
              <Button
                disabled={!rows.length}
                onClick={handleExportClick}
                size="small"
                startIcon={<DownloadIcon />}
                sx={{
                  ...filterButtonSx,
                  backgroundColor: '#d5ad3f',
                  color: '#10263d',
                  fontWeight: 800,
                  '&:hover': { backgroundColor: '#c39b2e' },
                }}
                variant="contained"
              >
                Exporter
              </Button>
            </Box>
          </Box>
          <Collapse in={filtersVisible} timeout="auto" unmountOnExit>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Grid container spacing={1.25}>
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={directionOptions}
                value={pendingFilters.directions}
                onChange={(_, value) => setPendingFilters((prev) => ({ ...prev, directions: value }))}
                renderInput={(params) => (
                  <TextField {...params} label="Direction" size="small" sx={filterFieldSx} variant="outlined" />
                )}
                className={`${classes.autocomplete} ${classes.smallAutopComplete}`}
                ListboxProps={{ className: classes.smallAutocompleteListbox }}
                limitTags={6}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={fundingOptions}
                value={pendingFilters.financements}
                onChange={(_, value) => setPendingFilters((prev) => ({ ...prev, financements: value }))}
                renderInput={(params) => (
                  <TextField {...params} label="Financement" size="small" sx={filterFieldSx} variant="outlined" />
                )}
                className={`${classes.autocomplete} ${classes.smallAutopComplete}`}
                ListboxProps={{ className: classes.smallAutocompleteListbox }}
                limitTags={4}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={statutOptions}
                value={pendingFilters.statuts}
                onChange={(_, value) => setPendingFilters((prev) => ({ ...prev, statuts: value }))}
                renderInput={(params) => (
                  <TextField {...params} label="Statut juridique" size="small" sx={filterFieldSx} variant="outlined" />
                )}
                className={`${classes.autocomplete} ${classes.smallAutopComplete}`}
                ListboxProps={{ className: classes.smallAutocompleteListbox }}
                limitTags={5}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={situationOptions}
                value={pendingFilters.situations}
                onChange={(_, value) => setPendingFilters((prev) => ({ ...prev, situations: value }))}
                renderInput={(params) => (
                  <TextField {...params} label="Situation" size="small" sx={filterFieldSx} variant="outlined" />
                )}
                className={`${classes.autocomplete} ${classes.smallAutopComplete}`}
                ListboxProps={{ className: classes.smallAutocompleteListbox }}
                limitTags={3}
              />
            </Grid>
          </Grid>

          <Grid container spacing={1.25}>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={filterFieldSx}
                size="small"
                variant="outlined"
                value={pendingFilters.search}
                onChange={(event) => setPendingFilters((prev) => ({ ...prev, search: event.target.value }))}
                placeholder="Nom partenaire..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <TextField
                select
                fullWidth
                label="Trier par"
                size="small"
                variant="outlined"
                sx={filterFieldSx}
                value={pendingFilters.sortBy}
                onChange={(event) => setPendingFilters((prev) => ({ ...prev, sortBy: event.target.value }))}
              >
                <MenuItem value="name">Nom</MenuItem>
                <MenuItem value="nbMarches">Nb marchés</MenuItem>
                <MenuItem value="situation">Situation</MenuItem>
                <MenuItem value="evalResult">Évaluation</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <TextField
                select
                fullWidth
                label="Ordre"
                size="small"
                variant="outlined"
                sx={filterFieldSx}
                value={pendingFilters.order}
                onChange={(event) => setPendingFilters((prev) => ({ ...prev, order: event.target.value }))}
              >
                <MenuItem value="asc">▲ Croissant</MenuItem>
                <MenuItem value="desc">▼ Décroissant</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 1.25,
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                border: '1px solid #d5e0eb',
                borderRadius: '6px',
                display: 'flex',
                flex: 1,
                flexWrap: 'wrap',
                gap: 0.75,
                p: 1,
                width: '100%',
              }}
            >
              <Typography color="#52677c" fontSize={11} fontWeight={700} mr={0.5}>
                Grouper par :
              </Typography>
              {[
                ['none', 'Aucun'],
                ['direction', 'Direction'],
                ['financement', 'Financement'],
                ['statutJuridique', 'Statut juridique'],
                ['situation', 'Situation'],
              ].map(([value, label]) => (
                <Button
                  key={value}
                  onClick={() => setPendingFilters((prev) => ({ ...prev, groupBy: value }))}
                  size="small"
                  sx={groupButtonSx}
                  variant={pendingFilters.groupBy === value ? 'contained' : 'outlined'}
                >
                  {label}
                </Button>
              ))}
            </Box>

            <Box
              sx={{
                alignItems: 'center',
                border: '1px solid #d5e0eb',
                borderRadius: '6px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                p: 1,
                width: { xs: '100%', lg: 'auto' },
              }}
            >
              <Typography color="#52677c" fontSize={11} fontWeight={700}>
                Période du contrat :
              </Typography>
              <TextField
                type="date"
                size="small"
                variant="outlined"
                value={pendingFilters.dateFrom}
                onChange={(event) => setPendingFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
                sx={{ ...filterFieldSx, width: { xs: '100%', sm: 165 } }}
                InputLabelProps={{ shrink: true }}
              />
              <Typography color="#52677c" fontSize={12}>→</Typography>
              <TextField
                type="date"
                size="small"
                variant="outlined"
                value={pendingFilters.dateTo}
                onChange={(event) => setPendingFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
                sx={{ ...filterFieldSx, width: { xs: '100%', sm: 165 } }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
            </Box>
          </Collapse>
        </Paper>

        <Paper
            elevation={0}
            sx={{ border: '1px solid #cbd8e6', borderRadius: '8px', overflow: 'hidden' }}
          >
            <div className={classes.tableHeader}>
              <div className={classes.tableTitle}>Liste des partenaires — 1 ligne = 1 partenaire</div>
              <div className={classes.tableRight}>
                <div className={classes.tableCount}>{rows.length} partenaire(s)</div>
              </div>
            </div>
            <Box sx={{ height: 560 }}>
              <DataGrid
                rows={displayedRows}
                columns={columns}
                columnHeaderHeight={48}
                disableColumnSorting={appliedFilters.groupBy !== 'none'}
                disableRowSelectionOnClick
                getRowClassName={(params) => params.row.isGroupHeader ? 'partner-group-row' : ''}
                hideFooter={appliedFilters.groupBy !== 'none'}
                localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                onPaginationModelChange={
                  appliedFilters.groupBy === 'none' ? setPaginationModel : undefined
                }
                onRowDoubleClick={(params) => {
                  if (params.row.isGroupHeader || (!params.row.partenaireId && params.row.partenaire === '-')) return;
                  handleOpenEvaluationDialog(
                    params.row.partenaire,
                    params.row.partenaireId,
                    params.row.partnerPid,
                    params.row.sourceDb
                  );
                }}
                pageSizeOptions={[10, 15, 30, 50]}
                pagination={appliedFilters.groupBy === 'none'}
                paginationModel={
                  appliedFilters.groupBy === 'none' ? paginationModel : undefined
                }
                rowHeight={48}
                sx={partnerGridSx}
              />
            </Box>
            {appliedFilters.groupBy !== 'none' && (
              <Box
                sx={{
                  alignItems: 'center',
                  borderTop: '1px solid #d5e0eb',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1.5,
                  justifyContent: 'space-between',
                  px: 1.5,
                  py: 1,
                }}
              >
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                  <Typography color="#52677c" fontSize={11}>
                    Cible partenaires par page
                  </Typography>
                  <TextField
                    onChange={(event) => setPaginationModel({
                      page: 0,
                      pageSize: Number(event.target.value),
                    })}
                    select
                    size="small"
                    sx={{ ...filterFieldSx, width: 82 }}
                    value={paginationModel.pageSize}
                  >
                    {[10, 15, 30, 50].map((size) => (
                      <MenuItem key={size} value={size}>{size}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                  <Typography color="#52677c" fontSize={11}>
                    Page {Math.min(paginationModel.page + 1, Math.max(groupedPages.length, 1))}
                    {' / '}
                    {Math.max(groupedPages.length, 1)}
                  </Typography>
                  <Pagination
                    count={Math.max(groupedPages.length, 1)}
                    onChange={(_, page) => setPaginationModel((current) => ({
                      ...current,
                      page: page - 1,
                    }))}
                    page={Math.min(paginationModel.page + 1, Math.max(groupedPages.length, 1))}
                    shape="rounded"
                    size="small"
                  />
                </Box>
              </Box>
            )}
          </Paper>
      </Box>
      <PartnerEvalution
        open={isEvaluationDialogOpen}
        onClose={handleCloseEvaluationDialog}
        partnerId={selectedPartnerId}
        partnerPid={selectedPartnerPid}
        partnerName={selectedPartnerName}
        sourceDb={selectedPartnerSourceDb}
      />
      <ExportContainer exportAnchorEl={exportAnchorEl} handleExportClose={handleExportClose} rows={rows} columns={columns}
      handlePrint={handlePrint} handleExcelExport={handleExcelExport} handleCsvExport={handleCsvExport} handlePdfExport={handlePdfExport} />
    </PartnerMainContainer>
  );
};

export default PartenersList;
